import { useState, useCallback } from "react";

// Focus-and-expand exploration state for the knowledge graph.
export function useExploreState() {
  const [rootId, setRootId] = useState(null);
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [selectedId, setSelectedId] = useState(null);
  const [panelOpen, setPanelOpen] = useState(false);
  const [breadcrumb, setBreadcrumb] = useState([]);

  const init = useCallback((id) => {
    setRootId(id);
    setExpandedIds(new Set([id]));
    setBreadcrumb([id]);
    setSelectedId(null);
    setPanelOpen(false);
  }, []);

  const focus = useCallback((id) => {
    setRootId((prevRoot) => {
      if (id !== prevRoot) {
        setBreadcrumb((bc) => (bc[bc.length - 1] === id ? bc : [...bc, id]));
      }
      return id;
    });
    setExpandedIds(new Set([id]));
    setSelectedId(id);
    setPanelOpen(true);
  }, []);

  const goToBreadcrumb = useCallback((index) => {
    setBreadcrumb((bc) => {
      const next = bc.slice(0, index + 1);
      const id = next[next.length - 1];
      setRootId(id);
      setExpandedIds(new Set([id]));
      setSelectedId(id);
      setPanelOpen(true);
      return next;
    });
  }, []);

  const select = useCallback((id) => {
    setSelectedId(id);
    setPanelOpen(true);
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const expand = useCallback((id) => {
    if (!id) return;
    setExpandedIds((prev) => {
      const next = new Set(prev);
      next.add(id);
      return next;
    });
  }, []);

  const jumpTo = useCallback((id) => select(id), [select]);

  const closePanel = useCallback(() => {
    setPanelOpen(false);
    setSelectedId(null);
  }, []);

  return {
    rootId,
    expandedIds,
    selectedId,
    panelOpen,
    breadcrumb,
    init,
    focus,
    goToBreadcrumb,
    select,
    expand,
    jumpTo,
    closePanel,
  };
}
